import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ApiContext } from "../../../context/MyContext";
import { useUtils } from "../../../context/UtilsContext";
import { bookingRecordsCache } from "../../../utils/listCache/caches";
import { resolveBookingEventTypeId } from "../../../pages/comms/Bookings/bookingEventType";
import { fetchEmployeeLeaves, modifyEmployeeLeaves } from "../core/leaveApi";
import { LEAVE_STATUS, leaveStatusIdFor, leaveStatusToken } from "../core/leaveLookups";
import { activeBookingsInLeave, loadBookings } from "../core/leaveBookings";
import { friendlyLeaveError } from "../core/leaveErrors";

/**
 * Leave requests a manager reviews: their direct reports' leaves (every
 * leave, for an executive) — the backend scopes `/fetch-employee-leave` that
 * way when no `employee_id` is passed. The caller's own leaves are dropped;
 * those belong on their profile, and nobody approves their own.
 *
 * Each pending leave carries the client bookings approving it would cancel.
 * They come from one `/fetch-booking-records` read across every pending
 * window, which the backend already scopes to the coaches this caller
 * manages, rather than one read per row.
 *
 * @param {{ enabled: boolean }} options
 * @returns {{
 *   rows: Array<Object>, loading: boolean, error: string|null, reload: Function,
 *   bookingsLoading: boolean, bookingsError: boolean, pendingCount: number,
 *   decide: (leave: Object, decision: 'approved'|'rejected', note: string) => Promise<{ cancelled: number }>,
 * }} `rows` are leave rows plus `bookings` (pending rows only; null until loaded)
 *   and `cancelledCount` (set once this session approves the row).
 */
export function useTeamLeaves({ enabled }) {
  const { get, post, employeeData } = useContext(ApiContext) || {};
  const { sessionUtils, corporateUtils, fetchCorporateUtils } = useUtils() || {};
  const employeeId = employeeData?.data?.employee_id || null;
  const eventTypeId = resolveBookingEventTypeId(sessionUtils);
  const active = Boolean(enabled && eventTypeId);

  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(active);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [bookingRecords, setBookingRecords] = useState(null);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState(false);
  // leave id → bookings cancelled by approving it, from this session's writes.
  const [cancelledByLeave, setCancelledByLeave] = useState({});
  const [lookupsAttempted, setLookupsAttempted] = useState(false);

  // Status ids ride on corporateUtils; a session copy from before the leave
  // lookups existed lacks them, so that one is refetched once.
  const hasLookups = Array.isArray(corporateUtils?.leave_statuses);
  useEffect(() => {
    if (!enabled || hasLookups || lookupsAttempted) return;
    if (typeof fetchCorporateUtils !== "function") {
      setLookupsAttempted(true);
      return;
    }
    Promise.resolve(fetchCorporateUtils({ force: Boolean(corporateUtils) }))
      .catch(() => null)
      .finally(() => setLookupsAttempted(true));
  }, [enabled, hasLookups, lookupsAttempted, fetchCorporateUtils]);

  useEffect(() => {
    if (!active) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchEmployeeLeaves(get, { eventTypeId })
      .then((rows) => {
        if (cancelled) return;
        setLeaves(rows.filter((l) => !employeeId || String(l.employee_id) !== String(employeeId)));
      })
      .catch((err) => {
        if (!cancelled) setError(friendlyLeaveError(err, { action: "load" }));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, eventTypeId, employeeId, reloadToken]);

  // One window covering every pending leave that hasn't ended yet.
  const bookingWindow = useMemo(() => {
    const now = Date.now();
    const pending = leaves.filter(
      (l) =>
        leaveStatusToken(l.status) === LEAVE_STATUS.PENDING && new Date(l.end_date).getTime() > now
    );
    if (!pending.length) return null;
    const start = Math.max(now, Math.min(...pending.map((l) => new Date(l.start_date).getTime())));
    const end = Math.max(...pending.map((l) => new Date(l.end_date).getTime()));
    if (Number.isNaN(start) || Number.isNaN(end)) return null;
    return { from: new Date(start).toISOString(), to: new Date(end).toISOString() };
  }, [leaves]);
  const windowKey = bookingWindow ? `${bookingWindow.from}|${bookingWindow.to}` : "";

  useEffect(() => {
    if (!active || !bookingWindow) {
      setBookingRecords([]);
      return undefined;
    }
    let cancelled = false;
    setBookingsLoading(true);
    setBookingsError(false);
    loadBookings(get, { eventTypeId, ...bookingWindow })
      .then((records) => {
        if (!cancelled) setBookingRecords(records);
      })
      .catch(() => {
        if (!cancelled) {
          setBookingRecords(null);
          setBookingsError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setBookingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, eventTypeId, windowKey]);

  const rows = useMemo(
    () =>
      leaves.map((leave) => ({
        ...leave,
        bookings:
          leaveStatusToken(leave.status) === LEAVE_STATUS.PENDING && bookingRecords
            ? activeBookingsInLeave(bookingRecords, leave)
            : null,
        cancelledCount: cancelledByLeave[leave.id] ?? null,
      })),
    [leaves, bookingRecords, cancelledByLeave]
  );

  const pendingCount = useMemo(
    () => leaves.filter((l) => leaveStatusToken(l.status) === LEAVE_STATUS.PENDING).length,
    [leaves]
  );

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  /**
   * Approve or reject one leave. `note` is sent when present — the backend
   * ignores unknown fields (see the feature README), so the note is dropped
   * until the backend stores it; the decision itself still goes through.
   */
  const decide = useCallback(
    async (leave, decision, note) => {
      const statusId = leaveStatusIdFor(corporateUtils, decision);
      if (!statusId) throw new Error("Leave statuses are not loaded yet. Try again.");
      let result;
      try {
        result = await modifyEmployeeLeaves(post, {
          eventTypeId,
          leaves: [
            {
              operation: "ADD",
              id: leave.id,
              leave_status_id: statusId,
              ...(note ? { note } : {}),
            },
          ],
        });
      } catch (err) {
        throw new Error(friendlyLeaveError(err, { action: "decide", leaves }));
      }
      const cancelled = Array.isArray(result?.cancelled_bookings)
        ? result.cancelled_bookings.length
        : 0;
      if (decision === LEAVE_STATUS.APPROVED) {
        setCancelledByLeave((prev) => ({ ...prev, [leave.id]: cancelled }));
        // Approval cancelled bookings server side; drop cached booking months.
        if (cancelled) bookingRecordsCache.clear();
      }
      reload();
      return { cancelled };
    },
    [post, eventTypeId, corporateUtils, leaves, reload]
  );

  return {
    rows,
    loading,
    error: active || !enabled ? error : "Leave review isn't available for this account.",
    reload,
    bookingsLoading,
    bookingsError,
    pendingCount,
    decide,
  };
}
